import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, MapPin, Trophy, Plus, Shield, UserCheck, Loader2 } from 'lucide-react';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import Header from '@/components/Header';

interface Team {
  team_id: number;
  team_name: string;
  sport_type: string | null;
  team_description: string | null;
  location: string | null;
  team_logo: string | null;
  slug: string | null;
  member_count?: number;
  role?: string;
}

export default function MyTeams() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const { user: unifiedUser } = useUnifiedUser();

  useEffect(() => {
    if (unifiedUser) {
      loadMyTeams();
    } else {
      setLoading(false);
    }
  }, [unifiedUser]);

  const loadMyTeams = async () => {
    if (!unifiedUser) return;

    try {
      setLoading(true);
      
      // Get team IDs where user is a player (using user_id field)
      const { data: playerTeamData } = await supabase
        .from('team_players')
        .select('team_id')
        .eq('user_id', unifiedUser.id);

      // Get team IDs where user is a coach (using coach_id field)
      const { data: coachTeamData } = await supabase
        .from('team_coaches')
        .select('team_id')
        .eq('coach_id', unifiedUser.id);

      // Combine all unique team IDs
      const playerIds = (playerTeamData || []).map(t => t.team_id);
      const coachIds = (coachTeamData || []).map(t => t.team_id);
      const allTeamIds = [...new Set([...playerIds, ...coachIds])];

      if (allTeamIds.length === 0) {
        setTeams([]);
        setLoading(false);
        return;
      }

      // Fetch team details
      const { data: teamsData, error: teamsError } = await supabase
        .from('teams')
        .select('team_id, team_name, sport_type, team_description, location, team_logo, slug')
        .in('team_id', allTeamIds);

      if (teamsError) {
        console.error('Error fetching teams:', teamsError);
        throw teamsError;
      }

      // Build teams with roles
      const allTeams: Team[] = (teamsData || []).map(team => {
        const isPlayer = playerIds.includes(team.team_id);
        const isCoach = coachIds.includes(team.team_id);
        let role = 'Member';
        if (isPlayer && isCoach) {
          role = 'Player & Coach';
        } else if (isCoach) {
          role = 'Coach';
        } else if (isPlayer) {
          role = 'Player';
        }
        return {
          ...team,
          role
        };
      });

      // Fetch member counts
      for (const team of allTeams) {
        const { count } = await supabase
          .from('team_players')
          .select('*', { count: 'exact', head: true })
          .eq('team_id', team.team_id);
        team.member_count = count || 0;
      }

      setTeams(allTeams);
    } catch (error) {
      console.error('Error loading teams:', error);
      toast.error('Failed to load your teams');
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'Coach':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'Player':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'Player & Coach':
        return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  if (!unifiedUser) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background pt-20">
          <div className="container mx-auto px-4 py-8">
            <Card>
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">Please Log In</h2>
                <p className="text-muted-foreground mb-4">
                  You need to be logged in to view your teams.
                </p>
                <div className="flex gap-4 justify-center">
                  <Link to="/login">
                    <Button>Log In</Button>
                  </Link>
                  <Link to="/register">
                    <Button variant="outline">Sign Up</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background pt-20">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background pt-20">
        <div className="container mx-auto px-4 py-8 space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">My Teams</h1>
              <p className="text-muted-foreground mt-1">
                Teams you're part of as a player or coach
              </p>
            </div>
            <Link to="/teams-search">
              <Button className="gap-2">
                <Plus className="w-4 h-4" />
                Find Teams
              </Button>
            </Link>
          </div>

          {/* Teams Grid */}
          {teams.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {teams.map((team) => (
                <Link key={team.team_id} to={`/team/${team.slug || team.team_id}`}>
                  <Card className="h-full hover:border-primary/30 transition-all hover:shadow-lg cursor-pointer group">
                    {/* Team Image */}
                    <div 
                      className="h-40 bg-cover bg-center relative rounded-t-lg"
                      style={{
                        backgroundImage: team.team_logo 
                          ? `url(${team.team_logo})` 
                          : 'linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary)/0.7) 100%)'
                      }}
                    >
                      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors rounded-t-lg" />
                      
                      {/* Role Badge */}
                      <Badge className={`absolute top-3 right-3 ${getRoleBadgeColor(team.role || 'Player')}`}>
                        {team.role === 'Coach' && <Shield className="w-3 h-3 mr-1" />}
                        {team.role === 'Player' && <UserCheck className="w-3 h-3 mr-1" />}
                        {team.role}
                      </Badge>

                      {/* Sport Badge */}
                      {team.sport_type && (
                        <Badge className="absolute top-3 left-3 bg-sports-green text-black font-bold">
                          <Trophy className="w-3 h-3 mr-1" />
                          {team.sport_type}
                        </Badge>
                      )}
                    </div>

                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg group-hover:text-primary transition-colors">
                        {team.team_name}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="space-y-3">
                      {team.team_description && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {team.team_description}
                        </p>
                      )}

                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        {team.location && (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            <span className="truncate max-w-[150px]">{team.location}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          <span>{team.member_count || 0} members</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Teams Yet</h3>
                <p className="text-muted-foreground mb-4">
                  You're not part of any teams yet. Find and join a team to get started!
                </p>
                <Link to="/teams-search">
                  <Button className="gap-2">
                    <Plus className="w-4 h-4" />
                    Find Teams
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
